import React, { useState, useEffect, useCallback } from 'react';
import { ArrowRightLeft, Check, X, Clock, UserPlus, AlertCircle, Loader2 } from 'lucide-react';
import { API } from '../../App';

const card = 'bg-[#1A1A1A] border border-[#2A2A2A] rounded-lg p-4';
const btnPrimary = 'px-4 py-2 min-h-[44px] bg-[#FF4D00] text-[#0A0A0A] font-bold text-sm rounded hover:bg-[#E63E00] transition-colors';
const btnSecondary = 'px-4 py-2 min-h-[44px] border border-[#2A2A2A] text-[#A0A0A0] text-sm rounded hover:border-[#FF4D00] hover:text-[#F5F5F5] transition-colors';

const STATUS_COLORS = {
  pending: 'bg-[#F2A900]/20 text-[#F2A900]',
  accepted: 'bg-[#00D4FF]/20 text-[#00D4FF]',
  declined: 'bg-[#A61E17]/20 text-[#A61E17]',
  completed: 'bg-green-500/20 text-green-400',
};

export default function MigrationWizard({ workspaceId }) {
  const [candidates, setCandidates] = useState([]);
  const [migrations, setMigrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [initiating, setInitiating] = useState(null);
  const [tab, setTab] = useState('candidates');

  const fetchData = useCallback(async () => {
    try {
      const [cand, status] = await Promise.all([
        API.get('/api/enterprise/migration/candidates', { params: { workspace_id: workspaceId } }),
        API.get('/api/enterprise/migration/status', { params: { workspace_id: workspaceId } }),
      ]);
      setCandidates(cand.data.candidates || []);
      setMigrations(status.data.migrations || []);
    } catch { /* fail gracefully */ }
    setLoading(false);
  }, [workspaceId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const initiateMigration = async (userId, email) => {
    setInitiating(userId);
    try {
      await API.post('/api/enterprise/migration/initiate', { workspace_id: workspaceId, user_id: userId });
      fetchData();
    } catch (e) { alert(e.response?.data?.detail || 'Migration failed'); }
    setInitiating(null);
  };

  if (loading) return <div className="text-center py-10 text-[#A0A0A0]">Loading...</div>;

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-bold font-['Barlow_Condensed'] text-[#F5F1E8]">User Migration</h2>
      <p className="text-xs text-[#A0A0A0]">Migrate free/pro users into your enterprise workspace</p>

      <div className="flex gap-2">
        {['candidates', 'status'].map(t => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-3 py-1.5 text-xs font-mono rounded ${tab === t ? 'bg-[#FF4D00] text-[#0A0A0A]' : 'bg-[#1A1A1A] border border-[#2A2A2A] text-[#A0A0A0] hover:text-[#F5F1E8]'}`}>
            {t === 'candidates' ? 'Available Users' : 'Migration Status'}
          </button>
        ))}
      </div>

      {tab === 'candidates' && (
        <>
          {candidates.length === 0 ? (
            <div className={`${card} text-center`}>
              <UserPlus className="w-8 h-8 mx-auto mb-2 text-[#00D4FF] opacity-50" />
              <p className="text-sm text-[#A0A0A0]">No migration candidates found</p>
              <p className="text-xs text-[#A0A0A0] mt-1">Users with matching email domains will appear here</p>
            </div>
          ) : (
            <div className="space-y-2">
              {candidates.map(c => (
                <div key={c.id || c.user_id} className={`${card} flex items-center justify-between`}>
                  <div>
                    <span className="text-sm text-[#F5F1E8] font-mono">{c.email}</span>
                    <div className="text-xs text-[#A0A0A0]">
                      {c.name && <span>{c.name} · </span>}
                      <span>Plan: {c.plan || 'free'}</span>
                      {c.created_at && <span> · Joined {new Date(c.created_at).toLocaleDateString()}</span>}
                    </div>
                  </div>
                  <button onClick={() => initiateMigration(c.id || c.user_id, c.email)} disabled={initiating === (c.id || c.user_id)} className={btnPrimary}>
                    {initiating === (c.id || c.user_id) ? <Loader2 className="w-4 h-4 animate-spin" /> : <><ArrowRightLeft className="w-4 h-4 inline mr-1" />Invite to Migrate</>}
                  </button>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {tab === 'status' && (
        <>
          {migrations.length === 0 ? (
            <div className={`${card} text-center`}>
              <Clock className="w-8 h-8 mx-auto mb-2 text-[#F2A900] opacity-50" />
              <p className="text-sm text-[#A0A0A0]">No migrations initiated yet</p>
            </div>
          ) : (
            <div className="space-y-2">
              {migrations.map((m, i) => (
                <div key={i} className={`${card} flex items-center justify-between`}>
                  <div>
                    <span className="text-sm text-[#F5F1E8] font-mono">{m.email || m.user_email}</span>
                    <div className="text-xs text-[#A0A0A0]">
                      {m.initiated_at && <span>Initiated {new Date(m.initiated_at).toLocaleDateString()}</span>}
                    </div>
                  </div>
                  <span className={`px-2 py-0.5 rounded text-[10px] font-mono ${STATUS_COLORS[m.status] || 'text-[#A0A0A0]'}`}>
                    {m.status?.toUpperCase()}
                  </span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
